import {
    getAllUsersThunk,
    deleteUserThunk,
    deActivateUserThunk,
    addWalletToUserThunk,
    exportUsersThunk,
} from '@redux/features/user-listing.slice';
import { AnimatedListSkeleton, AnimatedTable, AnimatedTableBody, AnimatedTableRow } from 'components/animations';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import BreadCrumb from 'Common/BreadCrumb';
import Pagination from 'Common/Pagination';
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal';
import { dateFormatter } from 'helpers/formatter_helper';
import { useEffect, useState } from 'react';
import DebouncedInput from 'Shared/DebouncedInput';
import FilterInput from 'Shared/FilterInput';
import { toast } from 'react-toastify';
import AddWalletToUserModal from './AddWalletToUserModal';
import UpdateUserModal from './UpdateUserModal';
import { RequestStatus } from '@core/enums/request-status.enum';
import ExportButton from 'Shared/ExportButton';
import ViewPdfLink from 'components/ui/ViewPdfLink';
import { isPdfPath, resolveUploadFileUrl } from 'helpers/resolveUploadFileUrl';

const Users = () => {
    // -------------------------------------------------------------
    const [page, setPage] = useState<number>(1);
    const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
    const [userId, setUserId] = useState<string>('');
    const [togglingUsers, setTogglingUsers] = useState<Set<string>>(new Set());
    // Filters
    const [filterUserId, setFilterUserId] = useState<string>('');
    const [filterEmail, setFilterEmail] = useState<string>('');
    const [filterAccountType, setFilterAccountType] = useState<string>('');
    const [filterTier, setFilterTier] = useState<string>('');
    const [filterAccountStatus, setFilterAccountStatus] = useState<string>('');
    const [showWalletModal, setShowWalletModal] = useState<boolean>(false);
    const [walletAccountId, setWalletAccountId] = useState<string>('');
    const [showUpdateModal, setShowUpdateModal] = useState<boolean>(false);
    const [updateUserId, setUpdateUserId] = useState<string>('');
    // -------------------------------------------------------------
    const dispatch = useAppDispatch();
    // -------------------------------------------------------------
    const { users, loading } = useAppSelector((state: RootState) => state.userListingState);
    // -------------------------------------------------------------
    const changePage = (p: string) => {
        setPage(Number(p));
    };

    const renderFileCell = (filePath: string | undefined | null) => {
        if (!filePath || filePath === '-') {
            return <span className="text-gray-500">-</span>;
        }

        const lowerPath = filePath.toLowerCase();
        const isImage = /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(lowerPath);

        if (isPdfPath(filePath)) {
            return <ViewPdfLink path={filePath} />;
        }
        if (isImage) {
            const href = resolveUploadFileUrl(filePath);
            return (
                <a href={href} target="_blank" rel="noreferrer">
                    <img
                        src={href}
                        alt="file preview"
                        className="w-10 h-10 rounded-md object-cover border border-gray-200 hover:opacity-80 transition-opacity"
                        onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            target.parentElement!.innerHTML = `<span class="text-gray-500">-</span>`;
                        }}
                    />
                </a>
            );
        }

        return <span className="text-gray-500">-</span>;
    };

    // -------------------------------------------------------------
    // Convert filterAccountStatus string to boolean or undefined
    const getAccountStatusBoolean = (): boolean | undefined => {
        if (filterAccountStatus === '') return undefined; // Return undefined for "All Status"
        return filterAccountStatus === 'true';
    };

    useEffect(() => {
        dispatch(
            getAllUsersThunk({
                pageNumber: String(page - 1),
                pageSize: `10`,
                userId: filterUserId || '',
                tier: filterTier || '',
                accountType: filterAccountType || '',
                isAccountStatus: getAccountStatusBoolean(),
                email: filterEmail || '',
            })
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, filterUserId, filterEmail, filterTier, filterAccountType, filterAccountStatus]);
    // -------------------------------------------------------------

    const handleDeleteUser = (id: string) => {
        setShowDeleteModal(true);
        setUserId(id);
    };

    const handleConfirmDelete = async () => {
        if (userId) {
            try {
                await dispatch(deleteUserThunk({ userId })).unwrap();
                toast.success('User deleted successfully', { autoClose: 2000 });
                setShowDeleteModal(false);
                setUserId('');
                // Refresh the user list
                const pageSize = users?.pageable?.pageSize || 10;
                dispatch(
                    getAllUsersThunk({
                        pageNumber: String(page - 1),
                        pageSize: String(pageSize),
                        userId: filterUserId || '',
                        tier: filterTier || '',
                        accountType: filterAccountType || '',
                        isAccountStatus: getAccountStatusBoolean(),
                        email: filterEmail || '',
                    })
                );
            } catch (error: any) {
                toast.error(error?.message || 'Failed to delete user', { autoClose: 2000 });
            }
        }
    };

    const handleToggleUserStatus = async (userId: string, currentStatus: boolean) => {
        const userKey = userId.toString();
        setTogglingUsers((prev) => new Set(prev).add(userKey));

        try {
            const newStatus = !currentStatus;
            await dispatch(deActivateUserThunk({ userId, activate: newStatus })).unwrap();
            toast.success(`User ${newStatus ? 'activated' : 'deactivated'} successfully`, { autoClose: 2000 });
            // Refresh the user list
            const pageSize = users?.pageable?.pageSize || 10;
            dispatch(
                getAllUsersThunk({
                    pageNumber: String(page - 1),
                    pageSize: String(pageSize),
                    userId: filterUserId || '',
                    tier: filterTier || '',
                    accountType: filterAccountType || '',
                    isAccountStatus: getAccountStatusBoolean(),
                    email: filterEmail || '',
                })
            );
        } catch (error: any) {
            toast.error(error?.message || 'Failed to update user status', { autoClose: 2000 });
        } finally {
            setTogglingUsers((prev) => {
                const newSet = new Set(prev);
                newSet.delete(userKey);
                return newSet;
            });
        }
    };

    const handleOpenWalletModal = (accountId: string) => {
        setWalletAccountId(accountId);
        setShowWalletModal(true);
    };

    const handleOpenUpdateModal = (userId: string) => {
        setUpdateUserId(userId);
        setShowUpdateModal(true);
    };

    const handleExport = () => {
        const isActive = filterAccountStatus === '' ? undefined : filterAccountStatus === 'true';
        dispatch(exportUsersThunk({ accountType: filterAccountType || '', isActive: isActive ?? true })).then(
            (res: any) => {
                if (res.meta.requestStatus === RequestStatus.FULFILLED) {
                    const downloadUrl = res.payload?.exportRequest?.downloadUrl || res.payload?.exportRequest?.filePath;
                    console.log(res);
                    if (downloadUrl) {
                        const a = document.createElement('a');
                        a.href = downloadUrl;
                        a.download = 'Users.csv';
                        a.target = '_blank';
                        document.body.appendChild(a);
                        a.click();
                        a.remove();
                    } else {
                        toast.error('Download URL not found in response', { autoClose: 2000 });
                    }
                }
            }
        );
    };
    // -------------------------------------------------------------

    return (
        <>
            <ConfirmationModal
                show={showDeleteModal}
                handleClose={() => {
                    setShowDeleteModal(false);
                    setUserId('');
                }}
                onConfirm={handleConfirmDelete}
                title="Delete User"
                message="Are you sure you want to delete this user? This action cannot be undone."
                confirmButtonText="Delete"
                cancelButtonText="Cancel"
                id={userId}
            />
            <AddWalletToUserModal
                show={showWalletModal}
                handleClose={() => {
                    setShowWalletModal(false);
                    setWalletAccountId('');
                }}
                accountId={walletAccountId}
                onSuccess={() => {
                    // Refresh users list after wallet operation
                    dispatch(
                        getAllUsersThunk({
                            pageNumber: String(page - 1),
                            pageSize: `10`,
                            userId: filterUserId || '',
                            tier: filterTier || '',
                            accountType: filterAccountType || '',
                            isAccountStatus: getAccountStatusBoolean(),
                            email: filterEmail || '',
                        })
                    );
                }}
            />
            <UpdateUserModal
                show={showUpdateModal}
                handleClose={() => {
                    setShowUpdateModal(false);
                    setUpdateUserId('');
                }}
                userId={updateUserId}
            />
            <BreadCrumb pageTitle={`Users List`} />
            {/* -------------------------------------------------- */}
            <section className="py-5">
                <div className="bg-[#fff] p-3 flex flex-col gap-2">
                    <div className="flex w-full items-center justify-between">
                        <h3 className="font-extrabold text-[#bd902e]">Users List</h3>
                    </div>
                    {/* Filters Row */}
                    <div className="flex flex-wrap gap-4">
                        <div className="flex-1 min-w-[220px]">
                            <label className="mb-2 block text-sm font-medium text-slate-700">User ID</label>
                            <DebouncedInput
                                placeholder="Search by user ID"
                                searchValue={filterUserId}
                                searchFn={(v) => {
                                    setFilterUserId(v);
                                    // setPage(1);
                                }}
                            />
                        </div>
                        <div className="flex-1 min-w-[220px]">
                            <label className="mb-2 block text-sm font-medium text-slate-700">Email</label>
                            <DebouncedInput
                                placeholder="Search by email"
                                searchValue={filterEmail}
                                searchFn={(v) => {
                                    setFilterEmail(v);
                                    // setPage(1);
                                }}
                            />
                        </div>
                        <div className="flex-1 min-w-[220px]">
                            <label className="mb-2 block text-sm font-medium text-slate-700">Account Type</label>
                            <FilterInput
                                value={filterAccountType}
                                onChange={(v) => setFilterAccountType(v)}
                                // filterFn={() => setPage(1)}
                                options={[
                                    { value: '', label: 'All Account Types' },
                                    { value: 'PHARMACY', label: 'PHARMACY' },
                                    { value: 'RETAIL_STORE', label: 'RETAIL STORE' },
                                ]}
                                id="accountType"
                                placeholder="All Account Types"
                                optionLabelKey="label"
                                optionValueKey="value"
                            />
                        </div>
                        <div className="flex-1 min-w-[220px]">
                            <label className="mb-2 block text-sm font-medium text-slate-700">Tier</label>
                            <FilterInput
                                value={filterTier}
                                onChange={(v) => setFilterTier(v)}
                                // filterFn={() => setPage(1)}
                                options={[
                                    { value: '', label: 'All Tiers' },
                                    { value: 'NO_TIER', label: 'NO TIER' },
                                    { value: 'TIER1', label: 'TIER 1' },
                                    { value: 'TIER2', label: 'TIER 2' },
                                    { value: 'TIER3', label: 'TIER 3' },
                                ]}
                                id="tier"
                                placeholder="All Tiers"
                                optionLabelKey="label"
                                optionValueKey="value"
                            />
                        </div>
                        <div className="flex-1 min-w-[220px]">
                            <label className="mb-2 block text-sm font-medium text-slate-700">Account Status</label>
                            <FilterInput
                                value={filterAccountStatus}
                                onChange={(v) => setFilterAccountStatus(v)}
                                // filterFn={() => setPage(1)}
                                options={[
                                    { value: '', label: 'All Status' },
                                    { value: 'true', label: 'ACTIVE' },
                                    { value: 'false', label: 'INACTIVE' },
                                ]}
                                id="accountStatus"
                                placeholder="All Status"
                                optionLabelKey="label"
                                optionValueKey="value"
                            />
                        </div>
                        <div className="flex-1 min-w-[100px] flex items-end">
                            <ExportButton onExport={handleExport} />
                        </div>
                    </div>
                    {loading ? (
                        <AnimatedListSkeleton rows={8} />
                    ) : (
                        <>
                            <div>
                                <div className="overflow-x-auto">
                                    <AnimatedTable className="table-custom w-full border-separate border-spacing-y-1 whitespace-nowrap">
                                        <thead className="ltr:text-left rtl:text-right">
                                            <tr
                                                className="relative rounded-md bg-slate-50 after:absolute after:bottom-0
                    after:left-0 after:top-0 after:border-l-2
                    after:border-transparent [&.active]:after:border-custom-500"
                                            >
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">User ID</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Account ID</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Email</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Account Type</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">business Name</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">business Info</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">
                                                    User Wallet Balance
                                                </th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">
                                                    business Licence Expiration Date
                                                </th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">
                                                    business Licence Number
                                                </th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">
                                                    business Licence Path
                                                </th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">
                                                    dea Certificate Path
                                                </th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">
                                                    resale Certificate Path
                                                </th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">
                                                    state Pharmacy Licence Path
                                                </th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">dea Number</th>
                                                <th className="px-3.5 py-2.5 font-semibold"> fax Number</th>
                                                <th className="px-3.5 py-2.5 font-semibold"> federal Tax ID</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">phone Number</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">
                                                    primary Contact Name
                                                </th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">
                                                    registration Date
                                                </th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">
                                                    address Line 1
                                                </th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">
                                                    address Line 2
                                                </th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">city</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">state</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">zip Code</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Details</th>
                                            </tr>
                                        </thead>
                                        <AnimatedTableBody loading={loading} itemCount={users?.content?.length ?? 0} animationKey={`users-${page}-${filterUserId}-${filterEmail}-${filterAccountType}-${filterTier}-${filterAccountStatus}`}>
                                            {users?.content?.map((user: any) => (
                                                <AnimatedTableRow
                                                    rowKey={user?.id}
                                                    detailRecord={user}
                                                    className="relative rounded-md bg-slate-50 after:absolute after:bottom-0
                        after:left-0 after:top-0 after:border-l-2 hover:bg-[#bd902e10]
                        after:border-transparent [&.active]:after:border-custom-500"
                                                >
                                                    <td className="px-3.5 py-2.5 font-mono">{user?.id || '-'}</td>
                                                    <td className="px-3.5 py-2.5 font-mono">{user?.account?.accountId || '-'}</td>
                                                    <td className="px-3.5 py-2.5 text-center">{`${user?.email || ``} `}</td>

                                                    <td className="px-3.5 py-2.5">
                                                        {user?.account?.accountType || `-`}
                                                    </td>
                                                    <td className="px-3.5 py-2.5">
                                                        {user?.account?.businessName || `-`}
                                                    </td>
                                                    <td className="px-3.5 py-2.5">
                                                        {user?.account?.businessInfo || `-`}
                                                    </td>
                                                    <td className="px-3.5 py-2.5">
                                                        {user?.account?.wallet?.balance || `-`}
                                                    </td>
                                                    <td className="px-3.5 py-2.5">
                                                        {dateFormatter(user?.account?.businessLicenceExpirationDate) ||
                                                            `-`}
                                                    </td>

                                                    <td className="px-3.5 py-2.5">
                                                        {user?.account?.businessLicenceNumber || `-`}
                                                    </td>
                                                    <td className="px-3.5 py-2.5">
                                                        {renderFileCell(user?.account?.businessLicencePath)}
                                                    </td>
                                                    <td className="px-3.5 py-2.5">
                                                        {renderFileCell(user?.account?.deaCertificatePath)}
                                                    </td>
                                                    <td className="px-3.5 py-2.5">
                                                        {renderFileCell(user?.account?.resaleCertificatePath)}
                                                    </td>
                                                    <td className="px-3.5 py-2.5">
                                                        {renderFileCell(user?.account?.statePharmacyLicencePath)}
                                                    </td>
                                                    <td className="px-3.5 py-2.5">{user?.account?.deaNumber || `-`}</td>
                                                    <td className="px-3.5 py-2.5">{user?.account?.faxNumber || `-`}</td>
                                                    <td className="px-3.5 py-2.5">
                                                        {user?.account?.federalTaxId || `-`}
                                                    </td>
                                                    <td className="px-3.5 py-2.5">
                                                        {user?.account?.phoneNumber || `-`}
                                                    </td>
                                                    <td className="px-3.5 py-2.5">
                                                        {user?.account?.primaryContactName || `-`}
                                                    </td>
                                                    <td className="px-3.5 py-2.5">
                                                        {user?.account?.shippingAddress?.registrationDate || `-`}
                                                    </td>
                                                    <td className="px-3.5 py-2.5">
                                                        {user?.account?.shippingAddress?.addressLine1 || `-`}
                                                    </td>
                                                    <td className="px-3.5 py-2.5">
                                                        {user?.account?.shippingAddress?.addressLine2 || `-`}
                                                    </td>
                                                    <td className="px-3.5 py-2.5">
                                                        {user?.account?.shippingAddress?.city || `-`}
                                                    </td>
                                                    <td className="px-3.5 py-2.5">
                                                        {user?.account?.shippingAddress?.state || `-`}
                                                    </td>
                                                    <td className="px-3.5 py-2.5">
                                                        {user?.account?.shippingAddress?.zipCode || `-`}
                                                    </td>
                                                    <td className="px-3.5 py-2.5">
                                                        <div className="flex flex-col justify-center items-center gap-2.5">
                                                            <div className="flex items-center gap-2 mb-2">
                                                                <span className="text-xs font-medium text-gray-700">
                                                                    Active:
                                                                </span>
                                                                <div className="relative inline-block w-10 align-middle transition duration-200 ease-in">
                                                                    <input
                                                                        type="checkbox"
                                                                        name={`toggle-${user?.id}`}
                                                                        id={`toggle-${user?.id}`}
                                                                        className="peer/toggle arrow-none absolute block size-5 cursor-pointer appearance-none rounded-full border-2 border-slate-200 bg-white/80 transition duration-300 ease-linear checked:border-yellow-600 checked:bg-white checked:bg-none ltr:checked:right-0 rtl:checked:left-0 disabled:opacity-50 disabled:cursor-not-allowed"
                                                                        checked={
                                                                            user?.account?.isAccountStatus || false
                                                                        }
                                                                        onChange={() =>
                                                                            handleToggleUserStatus(
                                                                                user?.id?.toString() || '',
                                                                                user?.account?.isAccountStatus || false
                                                                            )
                                                                        }
                                                                        disabled={togglingUsers.has(
                                                                            user?.id?.toString() || ''
                                                                        )}
                                                                    />
                                                                    <label
                                                                        htmlFor={`toggle-${user?.id}`}
                                                                        className="block h-5 cursor-pointer overflow-hidden rounded-full border border-slate-200 bg-slate-200 duration-300 ease-linear peer-checked/toggle:border-yellow-600 peer-checked/toggle:bg-yellow-600"
                                                                    ></label>
                                                                </div>
                                                            </div>
                                                            <button
                                                                className="mx-auto group flex w-full px-2 text-xs font-semibold py-1 items-center justify-center
                                                    rounded-md border border-yellow-600
                                                    bg-white transition-all duration-300 ease-linear hover:scale-105"
                                                                onClick={() => {
                                                                    handleDeleteUser(user?.id?.toString() || '');
                                                                }}
                                                            >
                                                                Delete User
                                                            </button>
                                                            <button
                                                                className="mx-auto group flex w-full px-2 text-xs font-semibold py-1 items-center justify-center
                                                    rounded-md border border-yellow-600
                                                    bg-white transition-all duration-300 ease-linear hover:scale-105"
                                                                onClick={() => {
                                                                    handleOpenWalletModal(
                                                                        user?.account?.accountId?.toString() || ''
                                                                    );
                                                                }}
                                                            >
                                                                Add wallet
                                                            </button>
                                                            <button
                                                                className="mx-auto group flex w-full px-2 text-xs font-semibold py-1 items-center justify-center
                                                    rounded-md border border-yellow-600
                                                    bg-white transition-all duration-300 ease-linear hover:scale-105"
                                                                onClick={() => {
                                                                    handleOpenUpdateModal(user?.id.toString() || '');
                                                                }}
                                                            >
                                                                Update User
                                                            </button>
                                                        </div>
                                                    </td>
                                                </AnimatedTableRow>
                                            ))}
                                        </AnimatedTableBody>
                                    </AnimatedTable>
                                </div>
                                <Pagination
                                    perPageData={users?.pageable?.pageSize || 10}
                                    total={users?.totalElements || 0}
                                    currentPage={page}
                                    changePage={changePage}
                                    length={users?.numberOfElements || users?.content?.length || 0}
                                />
                            </div>
                        </>
                    )}
                </div>
            </section>
            {/* -------------------------------------------------- */}
        </>
    );
};
export default Users;
